/**
 * Catalog service class.
 *
 * Caches + dedups requests against `@pressreader/catalog-publications`
 * and `@pressreader/catalog-nav-groups-api`.
 */

import isDate from 'lodash/isDate';
import keyBy from 'lodash/keyBy';

import { getCategoryGroup, getLocales, ICatalogGroupDto } from '@pressreader/catalog-nav-groups-api';
import {
    findPublications,
    getCatalogMetadata,
    getPublicationDTO,
    IIssueDto,
    IPublicationDto,
    IPublicationsQuery,
} from '@pressreader/catalog-publications';
import { CatalogMetaDataDto, CategoryGroupName, ICatalogLocale, ICatalogNavGroup } from '@pressreader/catalog-types';
import { error } from '@pressreader/logger';
import { localizedString } from '@pressreader/resources';
import { svcAuth } from '@pressreader/services';
import { IPagedResult } from '@pressreader/types';
import { parseDateString, visibility } from '@pressreader/utils';

/** Catalog-version poll: server rebuilds happen daily-ish; 15 min keeps long sessions reasonably fresh without hammering. */
const CATALOG_VERSION_POLL_INTERVAL = 15 * 60 * 1000;

function logReject<T>(err: unknown): Promise<T> {
    error(err);
    return Promise.reject(err);
}

// there is special case with synthetic categories
// they cannot be localized on server side for the time being
// it's planned, but not for now
// so as a temporary workaround let's localize them here - on the lowermost possible UI layer
let localizations: Record<string, string> | undefined;

function ensureLocalizations() {
    if (!localizations) {
        localizations = {
            PublicationTypes: localizedString('v7.Client.Catalog.types'),
            Newspapers: localizedString('v7.Client.Catalog.newspapers'),
            Magazines: localizedString('v7.Client.Catalog.magazines'),
        };
    }
}

class CatalogService {
    private _localesCache: ICatalogLocale[] | null = null;
    private _publicationCache: Record<string, IPublicationDto> = {};
    private _groupCache: Record<string, ICatalogGroupDto> = {};
    private _pendingRequests: Record<string, Promise<unknown>> = {};
    private _latestMetadata: CatalogMetaDataDto | null = null;
    private _pollHandle?: { dispose(): void };

    /**
     * Build the service and seed the catalog-version baseline immediately: the poll's first fire is
     * a full interval away and only baselines (`_latestMetadata === null` skips the compare), so
     * without the seed a server rebuild inside that first window would go undetected — the poll
     * would adopt the post-rebuild version as baseline while the caches still hold pre-rebuild data.
     */
    static create(): CatalogService {
        const service = new CatalogService();
        void service._checkUpdates();
        return service;
    }

    constructor() {
        // Two invalidation triggers:
        //  1. `svcAuth.tokenUpdated` — fires when the JWT hash changes (login/logout/library
        //     activation/etc.), i.e. user-scoped catalog fields (isFree, availability, …) may differ.
        //  2. Periodic poll of `getCatalogMetadata` — catches server-side catalog rebuilds
        //     (new publications, removals, version bumps) that the JWT does NOT signal. Without
        //     this, long-lived sessions (kiosks, hotspots, etc.) hold stale catalog data until
        //     the user re-authenticates.
        this._invalidateCaches = this._invalidateCaches.bind(this);
        svcAuth.bind('tokenUpdated', this._invalidateCaches);
        // Pauses while the tab is hidden and re-fires on return if the interval already elapsed.
        this._pollHandle = visibility.setInterval(() => void this._checkUpdates(), CATALOG_VERSION_POLL_INTERVAL);
    }

    private _deduplicateRequest<T>(requestKey: string, requestFn: () => Promise<T>): Promise<T> {
        const pending = this._pendingRequests[requestKey];
        if (pending !== undefined) {
            return pending as Promise<T>;
        }

        const promise = requestFn();
        this._pendingRequests[requestKey] = promise;
        // Settle-callback instead of `.finally` — the workspace lib target (es2017) has no Promise.finally typings.
        const cleanup = () => {
            delete this._pendingRequests[requestKey];
        };
        promise.then(cleanup, cleanup);
        return promise;
    }

    /**
     * Cache-check → dedup → fetch → normalize → store. Caches by `key` in the passed `cache` object,
     * dedups concurrent fetches by `requestKey`. Errors are logged + re-thrown via `logReject`.
     */
    private _cachedFetch<TRaw, T>(
        cache: Record<string, T>,
        key: string,
        requestKey: string,
        fetcher: () => Promise<TRaw>,
        normalizer: (raw: TRaw) => T,
    ): Promise<T> {
        if (cache[key] !== undefined) {
            return Promise.resolve(cache[key]);
        }
        return this._deduplicateRequest(requestKey, () =>
            fetcher()
                .then(normalizer)
                .then(value => {
                    cache[key] = value;
                    return value;
                })
                .catch(err => logReject<T>(err)),
        );
    }

    private _normalizeCategory(category: ICatalogGroupDto): ICatalogGroupDto {
        return Object.freeze({
            ...category,
            displayName: localizations?.[category.name] || category.displayName,
        });
    }

    private _normalizeCategoryGroup(group: ICatalogGroupDto): ICatalogGroupDto {
        return Object.freeze({
            ...group,
            displayName: localizations?.[group.name] || group.displayName,
            categories: group.categories.map(c => this._normalizeCategory(c)),
        });
    }

    private _normalizeIssue(issue: IIssueDto | null | undefined): IIssueDto | null {
        if (!issue) {
            return null;
        }
        return Object.freeze({
            ...issue,
            // Unparseable dates are cached as null (pre-move behavior; consumers re-parse via
            // mapPublicationFromDto) even though IIssueDto declares `Date | string`.
            issueDate: (isDate(issue.issueDate) ? issue.issueDate : parseDateString(issue.issueDate)) as IIssueDto['issueDate'],
            firstPage: Object.freeze({ ...issue.firstPage }),
        });
    }

    private _normalizePublication(publication: IPublicationDto): IPublicationDto {
        return Object.freeze({
            ...publication,
            supplements: (publication.supplements || []).map(s => this._normalizePublication(s)),
            otherRegionalEditions: publication.otherRegionalEditions?.map(p => this._normalizePublication(p)),
            latestIssue: this._normalizeIssue(publication.latestIssue) as IIssueDto,
        });
    }

    /**
     * Caches a publication and its nested editions (supplements / regional editions) by their own cid —
     * each nested publication is also a top-level publication elsewhere; avoids a fresh GET on getPublication(child.cid).
     *
     * Writes through the `cache` reference passed by the caller, NOT `this._publicationCache` directly.
     * Callers snapshot `this._publicationCache` before the network round-trip so that a concurrent
     * `_invalidateCaches` (which replaces the field with a fresh `{}`) leaves the in-flight write
     * landing on the orphaned old object, not the new one.
     */
    private _cachePublication(cache: Record<string, IPublicationDto>, publication: IPublicationDto): IPublicationDto {
        cache[publication.cid] = publication;
        publication.supplements?.forEach(s => (cache[s.cid] = s));
        publication.otherRegionalEditions?.forEach(p => (cache[p.cid] = p));
        return publication;
    }

    /**
     * Gets publication by CID.
     */
    getPublication(cid: string): Promise<IPublicationDto> {
        const cache = this._publicationCache;
        return this._cachedFetch(
            cache,
            cid,
            `cid:${cid}`,
            () => getPublicationDTO(cid),
            p => this._cachePublication(cache, this._normalizePublication(p)),
        );
    }

    /**
     * Finds publications in accordance to given criteria.
     */
    async findPublications(query: IPublicationsQuery): Promise<IPagedResult<IPublicationDto>> {
        const cache = this._publicationCache;
        try {
            const raw = await findPublications(query);
            const result = {
                items: raw.items.map(p => this._normalizePublication(p)),
                meta: raw.meta,
            };
            result.items.forEach(p => this._cachePublication(cache, p));
            return result;
        } catch (err) {
            return logReject<IPagedResult<IPublicationDto>>(err);
        }
    }

    /**
     * Gets publications list by list of CID.
     *
     * Fetches only the cids not already in cache, then returns the full result set
     * from the (now-populated) cache in input order.
     */
    async getPublications(cids: string[]): Promise<IPublicationDto[]> {
        if (!Array.isArray(cids)) {
            throw new TypeError('"cids" argument has to be array');
        }
        if (cids.length === 0) {
            return [];
        }

        const missing = cids.filter(c => this._publicationCache[c] === undefined);
        if (missing.length === 0) {
            return cids.map(c => this._publicationCache[c]);
        }

        // findPublications populates _publicationCache as a side effect.
        await this.findPublications({
            filters: { cids: missing },
            offset: 0,
            limit: missing.length,
        });
        return cids.map(c => this._publicationCache[c]).filter(Boolean);
    }

    /**
     * Gets group of categories by group name.
     */
    getCategoryGroup(name: string): Promise<ICatalogGroupDto> {
        return this._cachedFetch(
            this._groupCache,
            name,
            `group:${name}`,
            () => getCategoryGroup(name),
            group => {
                ensureLocalizations();
                return this._normalizeCategoryGroup(group);
            },
        );
    }

    /**
     * Gets Languages categories list.
     */
    getLanguages(): Promise<ICatalogGroupDto[]> {
        return this.getCategoryGroup(CategoryGroupName.Languages).then(group => group.categories);
    }

    /**
     * Gets Countries categories list.
     */
    getCountries(): Promise<ICatalogGroupDto[]> {
        return this.getCategoryGroup(CategoryGroupName.Countries).then(group => group.categories);
    }

    /**
     * Gets Categories list.
     */
    getCategories(): Promise<ICatalogGroupDto[]> {
        return this.getCategoryGroup(CategoryGroupName.Categories).then(group => group.categories);
    }

    /**
     * Get available locales. Locale is a combination of country and language.
     */
    async getLocales(): Promise<ICatalogLocale[]> {
        if (this._localesCache !== null) {
            return this._localesCache;
        }
        try {
            const data = await getLocales();
            const countries = keyBy(data.countries, 'id');
            const languages = keyBy(data.languages, 'id');

            this._localesCache = data.locales.map(locale => {
                const country = Object.freeze(countries[locale[0]]) as ICatalogNavGroup;
                const language = Object.freeze(languages[locale[1]]) as ICatalogNavGroup;
                return Object.freeze({
                    code: `${country.iso}-${language.iso}`,
                    country,
                    language,
                });
            });

            return this._localesCache;
        } catch (err) {
            return logReject<ICatalogLocale[]>(err);
        }
    }

    private _invalidateCaches(): void {
        this._groupCache = {};
        this._publicationCache = {};
        this._localesCache = null;
        // Drop in-flight requests too: any pending fetch started before user-state change
        // would write a stale (old-user-scoped) row back into the freshly-cleared cache
        // when it settles. Their `.finally` cleanup is a no-op on the absent entry.
        this._pendingRequests = {};
    }

    /**
     * Polls server-side catalog version; invalidates caches when `metadata.version` changes.
     * Cadence + tab-visibility pausing are handled by `visibility.setInterval` (see constructor).
     */
    private async _checkUpdates(): Promise<void> {
        try {
            const metadata = await getCatalogMetadata();
            if (this._latestMetadata !== null && this._latestMetadata.version !== metadata.version) {
                this._invalidateCaches();
            }
            this._latestMetadata = metadata;
        } catch (err) {
            error(err);
        }
    }

    destroy(): void {
        this._pollHandle?.dispose();
        this._invalidateCaches();
        svcAuth.unbind('tokenUpdated', this._invalidateCaches);
    }
}

export default CatalogService;
